"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useRouter } from "next/navigation";
import { signIn, signOut, useSession } from "next-auth/react";
import { getAuth, type User } from "firebase/auth";
import * as Sentry from "@sentry/nextjs";
import { auth as firebaseAuth } from "../firebase/client";
import { trackLegacySessionUpgrade } from "../db-api-telemetry";
import { hasFirebaseUserId } from "../token-utils";
import { AuthProviderOptions } from "../types";
import {
  didAcceptLegacySessionUpgrade,
  type SessionUpdateResult,
} from "./legacy-session-upgrade";

declare global {
  interface Window {
    posthog?: any;
  }
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (provider: AuthProviderOptions, token: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type IdentifyUser = (
  userId: string,
  properties: Record<string, unknown>,
) => void;

type SessionUpdate = (data: {
  firebaseIdToken: string;
  firebaseRefreshToken?: string;
}) => Promise<SessionUpdateResult>;

type LegacyUpgradeTelemetry = {
  posthog: { apiKey?: string; host?: string };
  app?: string;
  email?: string | null;
  userId?: string;
  firebaseUserPresent: boolean;
  featureFlagEnabled: boolean;
};

async function buildFirebaseSessionUpdate(user: User) {
  const freshToken = await user.getIdToken();
  return {
    firebaseIdToken: freshToken,
    firebaseRefreshToken: user.refreshToken,
  };
}

function isLegacyDatabaseApiToken(token: string | undefined): boolean {
  return Boolean(token && hasFirebaseUserId(token));
}

function isLegacyAuthUpgradeEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ENABLE_LEGACY_AUTH_UPGRADE === "true";
}

function getTelemetryOptions() {
  return {
    apiKey: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  };
}

function getBrowserAppLabel(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.location.hostname || undefined;
}

function shouldConsiderLegacyUpgrade({
  skipTokenExchange,
  status,
  sessionAccessToken,
  isFirebaseAuthHydrated,
}: {
  skipTokenExchange?: boolean;
  status: string;
  sessionAccessToken?: string;
  isFirebaseAuthHydrated: boolean;
}): boolean {
  if (!skipTokenExchange) return false;
  if (status !== "authenticated") return false;
  if (!isLegacyDatabaseApiToken(sessionAccessToken)) return false;
  return isFirebaseAuthHydrated;
}

function getLegacyUpgradeAttemptKey({
  sessionAccessToken,
  firebaseUser,
  featureFlagEnabled,
}: {
  sessionAccessToken: string | undefined;
  firebaseUser: User | null;
  featureFlagEnabled: boolean;
}): string {
  return [
    sessionAccessToken,
    firebaseUser?.uid ?? "missing",
    featureFlagEnabled ? "enabled" : "disabled",
  ].join(":");
}

function buildLegacyUpgradeTelemetry({
  sessionUserEmail,
  sessionUserId,
  firebaseUser,
  featureFlagEnabled,
}: {
  sessionUserEmail?: string | null;
  sessionUserId?: string;
  firebaseUser: User | null;
  featureFlagEnabled: boolean;
}): LegacyUpgradeTelemetry {
  return {
    posthog: getTelemetryOptions(),
    app: getBrowserAppLabel(),
    email: sessionUserEmail,
    userId: sessionUserId,
    firebaseUserPresent: Boolean(firebaseUser),
    featureFlagEnabled,
  };
}

function trackLegacyUpgradeAttempt(telemetry: LegacyUpgradeTelemetry) {
  trackLegacySessionUpgrade(telemetry.posthog, {
    event: "auth_legacy_session_upgrade_attempted",
    app: telemetry.app,
    email: telemetry.email,
    userId: telemetry.userId,
    firebaseUserPresent: telemetry.firebaseUserPresent,
    featureFlagEnabled: telemetry.featureFlagEnabled,
  });
}

function trackLegacyUpgradeSkipped(
  telemetry: LegacyUpgradeTelemetry,
  reason: string,
) {
  trackLegacySessionUpgrade(telemetry.posthog, {
    event: "auth_legacy_session_upgrade_skipped",
    app: telemetry.app,
    email: telemetry.email,
    userId: telemetry.userId,
    firebaseUserPresent: telemetry.firebaseUserPresent,
    featureFlagEnabled: telemetry.featureFlagEnabled,
    reason,
  });
}

function trackLegacyUpgradeUnavailable(
  telemetry: LegacyUpgradeTelemetry,
  reason: string,
) {
  trackLegacySessionUpgrade(telemetry.posthog, {
    event: "auth_legacy_session_upgrade_unavailable",
    app: telemetry.app,
    email: telemetry.email,
    userId: telemetry.userId,
    firebaseUserPresent: telemetry.firebaseUserPresent,
    featureFlagEnabled: telemetry.featureFlagEnabled,
    reason,
  });
}

function trackLegacyUpgradeFailed(
  telemetry: LegacyUpgradeTelemetry,
  reason: string,
) {
  trackLegacySessionUpgrade(telemetry.posthog, {
    event: "auth_legacy_session_upgrade_failed",
    app: telemetry.app,
    email: telemetry.email,
    userId: telemetry.userId,
    firebaseUserPresent: true,
    featureFlagEnabled: telemetry.featureFlagEnabled,
    reason,
  });
}

async function runLegacySessionUpgrade({
  firebaseUser,
  update,
  telemetry,
  isCancelled,
}: {
  firebaseUser: User;
  update: SessionUpdate;
  telemetry: LegacyUpgradeTelemetry;
  isCancelled: () => boolean;
}) {
  try {
    const updateData = await buildFirebaseSessionUpdate(firebaseUser);
    if (isCancelled()) return;
    const updatedSession = await update(updateData);
    if (
      !didAcceptLegacySessionUpgrade({
        firebaseIdToken: updateData.firebaseIdToken,
        firebaseUserId: firebaseUser.uid,
        updatedSession,
      })
    ) {
      trackLegacyUpgradeFailed(telemetry, "session_update_rejected");
      return;
    }

    trackLegacySessionUpgrade(telemetry.posthog, {
      event: "auth_legacy_session_upgraded",
      app: telemetry.app,
      email: telemetry.email,
      userId: telemetry.userId,
      firebaseUserPresent: true,
      featureFlagEnabled: telemetry.featureFlagEnabled,
    });
  } catch (err) {
    console.error("Failed to upgrade legacy auth session:", err);
    trackLegacyUpgradeFailed(telemetry, "session_update_failed");
  }
}

function getDeviceType(): "mobile" | "desktop" {
  return /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(
    window.navigator.userAgent,
  )
    ? "mobile"
    : "desktop";
}

function buildUserProperties(sessionUser: {
  email?: string | null;
  name?: string | null;
}) {
  return {
    email: sessionUser.email || undefined,
    name: sessionUser.name || undefined,
    platform: window.navigator.platform,
    language: window.navigator.language,
    vendor: window.navigator.vendor,
    deviceType: getDeviceType(),
  };
}

function useFirebaseAuthState() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [isFirebaseAuthHydrated, setIsFirebaseAuthHydrated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("authToken");
      setIsAuthenticated(!!token);
    };

    const unsubscribe = getAuth().onAuthStateChanged((user) => {
      setFirebaseUser(user);
      setIsFirebaseAuthHydrated(true);
    });

    checkAuth();

    return () => unsubscribe();
  }, []);

  return {
    firebaseUser,
    isAuthenticated,
    isFirebaseAuthHydrated,
    setFirebaseUser,
    setIsAuthenticated,
  };
}

function useFirebaseSessionSync({
  skipTokenExchange,
  status,
  updateRef,
}: {
  skipTokenExchange?: boolean;
  status: string;
  updateRef: React.MutableRefObject<SessionUpdate>;
}) {
  const hasPushedTokenRef = useRef(false);
  const statusRef = useRef(status);
  statusRef.current = status;

  useEffect(() => {
    if (!skipTokenExchange) return;

    const unsubscribe = getAuth().onIdTokenChanged(async (user) => {
      // Skip the initial subscription fire; sign-in already put that token in
      // the session, and hydration may still report NextAuth as "loading".
      if (!hasPushedTokenRef.current) {
        hasPushedTokenRef.current = true;
        return;
      }

      if (!user || statusRef.current !== "authenticated") return;

      try {
        await updateRef.current(await buildFirebaseSessionUpdate(user));
      } catch (err) {
        console.error("Failed to push refreshed Firebase token:", err);
      }
    });

    return () => unsubscribe();
  }, [skipTokenExchange, updateRef]);
}

function useLegacySessionUpgrade({
  firebaseUser,
  isFirebaseAuthHydrated,
  sessionAccessToken,
  sessionUserEmail,
  sessionUserId,
  sessionProviderUserId,
  skipTokenExchange,
  status,
  updateRef,
}: {
  firebaseUser: User | null;
  isFirebaseAuthHydrated: boolean;
  sessionAccessToken?: string;
  sessionUserEmail?: string | null;
  sessionUserId?: string;
  sessionProviderUserId?: string;
  skipTokenExchange?: boolean;
  status: string;
  updateRef: React.MutableRefObject<SessionUpdate>;
}) {
  const legacyUpgradeAttemptKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      !shouldConsiderLegacyUpgrade({
        skipTokenExchange,
        status,
        sessionAccessToken,
        isFirebaseAuthHydrated,
      })
    ) {
      return;
    }
    const featureFlagEnabled = isLegacyAuthUpgradeEnabled();
    const attemptKey = getLegacyUpgradeAttemptKey({
      sessionAccessToken,
      firebaseUser,
      featureFlagEnabled,
    });
    if (legacyUpgradeAttemptKeyRef.current === attemptKey) return;
    legacyUpgradeAttemptKeyRef.current = attemptKey;

    const telemetry = buildLegacyUpgradeTelemetry({
      sessionUserEmail,
      sessionUserId,
      firebaseUser,
      featureFlagEnabled,
    });
    trackLegacyUpgradeAttempt(telemetry);

    if (!featureFlagEnabled) {
      trackLegacyUpgradeSkipped(telemetry, "feature_flag_disabled");
      return;
    }

    if (!firebaseUser) {
      trackLegacyUpgradeUnavailable(telemetry, "firebase_user_missing");
      return;
    }

    if (!sessionProviderUserId) {
      trackLegacyUpgradeUnavailable(telemetry, "provider_user_id_missing");
      return;
    }

    if (firebaseUser.uid !== sessionProviderUserId) {
      trackLegacyUpgradeUnavailable(telemetry, "firebase_user_mismatch");
      return;
    }

    let cancelled = false;
    void runLegacySessionUpgrade({
      firebaseUser,
      update: updateRef.current,
      telemetry,
      isCancelled: () => cancelled,
    });

    return () => {
      cancelled = true;
      if (legacyUpgradeAttemptKeyRef.current === attemptKey) {
        legacyUpgradeAttemptKeyRef.current = null;
      }
    };
  }, [
    firebaseUser,
    isFirebaseAuthHydrated,
    sessionAccessToken,
    sessionUserEmail,
    sessionUserId,
    sessionProviderUserId,
    skipTokenExchange,
    status,
    updateRef,
  ]);
}

function useSessionIdentity({
  identifyUser,
  sessionUserEmail,
  sessionUserId,
  sessionUserName,
  status,
}: {
  identifyUser?: IdentifyUser;
  sessionUserEmail?: string | null;
  sessionUserId?: string;
  sessionUserName?: string | null;
  status: string;
}) {
  const hasIdentifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (status !== "authenticated" || !sessionUserId) return;
    if (hasIdentifiedRef.current === sessionUserId) return;
    hasIdentifiedRef.current = sessionUserId;

    const userProperties = buildUserProperties({
      email: sessionUserEmail,
      name: sessionUserName,
    });

    Sentry.setUser({
      id: sessionUserId,
      email: sessionUserEmail || undefined,
      username: sessionUserName || undefined,
    });

    if (identifyUser) {
      identifyUser(sessionUserId, userProperties);
    }

    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.identify(sessionUserId, userProperties);
    }

    return () => {
      if (hasIdentifiedRef.current === sessionUserId) {
        hasIdentifiedRef.current = null;
      }
      Sentry.setUser(null);
    };
  }, [status, sessionUserId, sessionUserEmail, sessionUserName, identifyUser]);

  return () => {
    hasIdentifiedRef.current = null;
    Sentry.setUser(null);

    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.reset();
    }
  };
}

export function AuthProvider({
  children,
  identifyUser,
  skipTokenExchange,
}: {
  children: React.ReactNode;
  identifyUser?: IdentifyUser;
  skipTokenExchange?: boolean;
}) {
  const {
    firebaseUser,
    isAuthenticated,
    isFirebaseAuthHydrated,
    setFirebaseUser,
    setIsAuthenticated,
  } = useFirebaseAuthState();
  const { data: session, status, update } = useSession();
  const router = useRouter();

  // Ref to avoid depending on `update` (not referentially stable in NextAuth v5)
  const updateRef = useRef<SessionUpdate>(update as SessionUpdate);
  updateRef.current = update as SessionUpdate;

  const sessionAccessToken = session?.databaseApiAccessToken;
  const sessionUserEmail = session?.user?.email;
  const sessionUserId = session?.user?.id;
  const sessionUserName = session?.user?.name;
  const sessionProviderUserId = session?.user?.providerUserId;

  useFirebaseSessionSync({ skipTokenExchange, status, updateRef });
  useLegacySessionUpgrade({
    firebaseUser,
    isFirebaseAuthHydrated,
    sessionAccessToken,
    sessionUserEmail,
    sessionUserId,
    sessionProviderUserId,
    skipTokenExchange,
    status,
    updateRef,
  });
  const resetIdentityTracking = useSessionIdentity({
    identifyUser,
    sessionUserEmail,
    sessionUserId,
    sessionUserName,
    status,
  });

  const login = async (provider: AuthProviderOptions, token: string) => {
    if (
      provider === AuthProviderOptions.GOOGLE ||
      provider === AuthProviderOptions.APPLE ||
      provider === AuthProviderOptions.MICROSOFT
    ) {
      await signIn(provider);
    } else if (provider === AuthProviderOptions.CREDENTIALS) {
      await signIn("credentials", {
        token,
        redirect: false,
      });
    }

    setIsAuthenticated(true);
    router.push("/dashboard");
  };

  const logout = async () => {
    localStorage.removeItem("authToken");
    setIsAuthenticated(false);
    await signOut({
      redirect: false,
    });
    await firebaseAuth.signOut();
    setFirebaseUser(null);

    resetIdentityTracking();

    router.push("/auth/login");
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user: firebaseUser, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
